import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';

import createFuzzySearch from '@nozbe/microfuzz';
import { ProjectIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { CloseIcon, SearchIconSmall, SpinnerDarkIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useDebounce from 'hooks/useDebounce';
import { createProjectCategory } from 'services/resources';
import { ThemeProvider } from 'styled-components';

import {
  ProjectCategorySelectOptionContainer,
  ProjectCategorySelectOptionLabel,
  ProjectCategorySelectOptionsContainer,
  ProjectCategorySelectSelectedClientsContainer,
  ProjectCategorySelectSelectedClientContainer,
  ProjectCategorySelectSelectedClientLabel,
  ProjectCategorySelectWrapper,
  ProjectCategorySelectSelectedClientRemoveContainer,
  ProjectCategorySelectInput,
  ProjectCategorySelectOptionAdd,
  ProjectCategorySelectIconContainer,
  ProjectCategorySelectOptionAlreadyAdded,
} from './styled';
import useProjectCategories from './useProjectCategories';

interface ProjectCategorySelectProps {
  name?: string;

  placeholder?: string;

  onChange?: (values: any[]) => void;

  allowMultiple?: boolean;

  allowCreate?: boolean;

  preselectedValues?: any[];
}

export default function ProjectCategorySelect({
  placeholder = 'No project category selected',
  name,
  onChange,
  allowMultiple = false,
  allowCreate = false,
  preselectedValues = [],
}: ProjectCategorySelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const { data: projectCategories } = useProjectCategories();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedProjectCategories, setSelectedProjectCategories] =
    useState<any[]>(preselectedValues);
  const [currentInputValue, setCurrentInputValue] = useState<string>('');
  const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false);
  const [list, setList] = useState<any[]>([]);
  const debouncedValue = useDebounce(currentInputValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Backspace' &&
      currentInputValue.trim() === '' &&
      selectedProjectCategories.length > 0
    ) {
      const lastProjectCategory = selectedProjectCategories[selectedProjectCategories.length - 1];
      handleClientClick(lastProjectCategory);
    }
  };

  const handleCreateProjectCategory = async (params) => {
    const response = await createProjectCategory(params);
    return response.data;
  };

  const { mutate } = useMutation(handleCreateProjectCategory, {
    onMutate: () => setCurrentlySubmitting(true),
    onSettled: (data) => {
      setCurrentlySubmitting(false);
      handleClientClick(data);
    },
  });

  const handleClientClick = (projectCategory) => {
    let finalProjectCategories = [];

    const clientIndex = selectedProjectCategories.findIndex(
      (selectedProjectCategory) => selectedProjectCategory.uuid === projectCategory.uuid
    );

    finalProjectCategories =
      clientIndex === -1
        ? allowMultiple
          ? [...selectedProjectCategories, projectCategory]
          : [projectCategory]
        : selectedProjectCategories.filter(
            (selectedProjectCategory) => selectedProjectCategory.uuid !== projectCategory.uuid
          );

    setSelectedProjectCategories(finalProjectCategories);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalProjectCategories.map((projectCategory) => projectCategory.uuid)
      );
    }

    setCurrentInputValue('');
    onChange?.(finalProjectCategories);
    setIsOpen(false);
  };

  useOnClickOutside(wrapperRef, () => {
    if (isOpen) {
      formikContext.setFieldTouched(name, true, false);
    }
    setIsOpen(false);
  });

  const fuzzySearch = useMemo(() => {
    if (projectCategories?.project_categories) {
      return createFuzzySearch(projectCategories?.project_categories, { key: 'name' });
    }
  }, [projectCategories?.project_categories]);

  const fuzzySearchCallback = useCallback(
    (value) => {
      if (fuzzySearch) {
        return fuzzySearch(value).map((result) => result.item);
      }
    },
    [debouncedValue, fuzzySearch]
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInputValue(e.target.value);
  };

  const handleWrapperClick = () => {
    if (isOpen) return;

    inputRef.current.focus();
    setIsOpen(true);
  };

  useEffect(() => {
    if (fuzzySearch && debouncedValue.trim() !== '') {
      setList(fuzzySearchCallback(debouncedValue));
    } else {
      setList(projectCategories?.project_categories);
    }
  }, [debouncedValue, fuzzySearchCallback, projectCategories]);

  const handleClientRemoveClick = (
    projectCategory: any,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    const finalProjectCategories = selectedProjectCategories.filter(
      (selectedProjectCategory) => selectedProjectCategory.uuid !== projectCategory.uuid
    );

    setSelectedProjectCategories(finalProjectCategories);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalProjectCategories.map((projectCategory) => projectCategory.uuid)
      );
    }

    if (isOpen) {
      setIsOpen(false);
    }
    onChange?.(finalProjectCategories);
  };

  const newClient = useMemo(() => {
    if (
      allowCreate &&
      debouncedValue &&
      !projectCategories?.project_categories.find(
        (opt) => opt.name.toLowerCase() === debouncedValue.toLowerCase()
      )
    ) {
      return { value: debouncedValue, name: debouncedValue, isCreated: true };
    }

    return null;
  }, [debouncedValue, allowCreate, projectCategories?.project_categories]);

  const handleNewClientClick = (projectCategory) => {
    mutate(projectCategory);
    setCurrentInputValue('');
    setIsOpen(false);
  };

  const isProjectCategorySelected = useCallback(
    (projectCategory) => {
      return selectedProjectCategories.find(
        (selectedProjectCategory) => selectedProjectCategory.uuid === projectCategory.uuid
      );
    },
    [selectedProjectCategories]
  );

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  return (
    <ThemeProvider theme={{ isError }}>
      <ProjectCategorySelectWrapper ref={wrapperRef} onClick={handleWrapperClick}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        <ProjectCategorySelectSelectedClientsContainer>
          {selectedProjectCategories.map((projectCategory) => (
            <ProjectCategorySelectSelectedClientContainer key={projectCategory.uuid}>
              <ProjectIcon width={16} height={16} />
              <ProjectCategorySelectSelectedClientLabel>
                {projectCategory.name}
              </ProjectCategorySelectSelectedClientLabel>
              <ProjectCategorySelectSelectedClientRemoveContainer
                data-action="remove"
                onClick={(e) => handleClientRemoveClick(projectCategory, e)}
              >
                <CloseIcon width={9} height={9} fill="var(--color-text)" fillOpacity="1" />
              </ProjectCategorySelectSelectedClientRemoveContainer>
            </ProjectCategorySelectSelectedClientContainer>
          ))}
          {currentlySubmitting ? (
            <ProjectCategorySelectSelectedClientContainer key={'new-client'}>
              <ProjectIcon width={16} height={16} />
              <ProjectCategorySelectSelectedClientLabel loading>
                <SpinnerDarkIcon />
              </ProjectCategorySelectSelectedClientLabel>
            </ProjectCategorySelectSelectedClientContainer>
          ) : null}
          <ProjectCategorySelectInput
            ref={inputRef}
            placeholder={
              selectedProjectCategories.length > 0 || currentlySubmitting ? null : placeholder
            }
            value={currentInputValue}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
          />
        </ProjectCategorySelectSelectedClientsContainer>
        <ProjectCategorySelectIconContainer>
          <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
        </ProjectCategorySelectIconContainer>
        <AnimatePresence>
          {isOpen ? (
            <ProjectCategorySelectOptionsContainer>
              {newClient ? (
                <ProjectCategorySelectOptionContainer
                  key={newClient.value}
                  onClick={() => handleNewClientClick(newClient)}
                >
                  <ProjectCategorySelectOptionLabel>
                    {newClient.name}
                  </ProjectCategorySelectOptionLabel>
                  <ProjectCategorySelectOptionAdd>ADD NEW</ProjectCategorySelectOptionAdd>
                </ProjectCategorySelectOptionContainer>
              ) : null}
              {list?.map((projectCategory) => (
                <ProjectCategorySelectOptionContainer
                  key={projectCategory.uuid}
                  onClick={() => handleClientClick(projectCategory)}
                >
                  <ProjectCategorySelectOptionLabel
                    alreadyAdded={isProjectCategorySelected(projectCategory)}
                  >
                    {projectCategory.name}
                  </ProjectCategorySelectOptionLabel>
                  {isProjectCategorySelected(projectCategory) ? (
                    <ProjectCategorySelectOptionAlreadyAdded>
                      ALREADY ADDED
                    </ProjectCategorySelectOptionAlreadyAdded>
                  ) : null}
                </ProjectCategorySelectOptionContainer>
              ))}
            </ProjectCategorySelectOptionsContainer>
          ) : null}
        </AnimatePresence>
      </ProjectCategorySelectWrapper>
    </ThemeProvider>
  );
}
